import { Box, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../../../../../components/UIToolkit/Popups/Dialog";

interface PtoTypeChangeConfirmDialogProps {
	open: boolean;
	rawString: string;
	/** Display label of the PTO type the row is currently mapped to (the "from" side of the title). */
	currentPtoTypeLabel: string;
	/** Display label of the new PTO type the row is changing to. */
	ptoTypeLabel: string;
	/** Synced time-offs in the last 60 days that "update previous" would re-map. */
	countLast60: number;
	/** remapPrevious = true to also re-map the last 60 days; false to apply the new mapping going forward only. */
	onConfirm: (remapPrevious: boolean) => void;
	onClose: () => void;
}

const FORWARD = "forward";
const REMAP = "remap";

/**
 * Confirmation shown after an admin changes a raw type's PTO mapping while it has synced time-offs in
 * the last 60 days (DAT-200). The new mapping always applies going forward; the admin can also re-map
 * the last 60 days of already-synced time-offs. "Change from now on" is the safe default.
 */
export const PtoTypeChangeConfirmDialog: FC<PtoTypeChangeConfirmDialogProps> = ({
	open,
	rawString,
	currentPtoTypeLabel,
	ptoTypeLabel,
	countLast60,
	onConfirm,
	onClose,
}) => {
	const { t } = useTranslation();
	const [choice, setChoice] = useState<string>(FORWARD);

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("hris.mapping.ptoTypeConfirm.title", { rawString, currentPtoType: currentPtoTypeLabel, newPtoType: ptoTypeLabel })}
			dataTestId="hris-mapping-pto-type-confirm-dialog"
			confirm={t("hris.mapping.ptoTypeConfirm.confirm")}
			onConfirm={() => onConfirm(choice === REMAP)}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1, minWidth: 380 }}>
					<Typography variant="body2" color="text.secondary">
						{t("hris.mapping.ptoTypeConfirm.description")}
					</Typography>
					<RadioGroup value={choice} onChange={(e) => setChoice(e.target.value)}>
						<FormControlLabel
							value={FORWARD}
							control={<Radio data-testid="hris-mapping-pto-type-forward" />}
							label={t("hris.mapping.ptoTypeConfirm.forward")}
						/>
						<FormControlLabel
							value={REMAP}
							control={<Radio data-testid="hris-mapping-pto-type-remap" />}
							label={t("hris.mapping.ptoTypeConfirm.remap", { count: countLast60 })}
						/>
					</RadioGroup>
				</Box>
			}
		/>
	);
};
